import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
    fetchUserSystemClientAccess,
    updateUserSystemClientAccess,
} from "model/UserModel";
import "./client-access-add-edit-modal.scss";

const normalizeClientId = (value) => String(value ?? "").trim();

const isSameClientId = (left, right) =>
    normalizeClientId(left) === normalizeClientId(right);

const normalizeClientRow = (client = {}) => ({
    clientId: normalizeClientId(client?.clientId),
    clientName: String(client?.clientName ?? "").trim(),
    keyName: String(client?.keyName ?? client?.keyname ?? client?.clientId ?? "").trim(),
    isAssigned: Boolean(client?.isAssigned),
    expected:
        client?.expected === null || client?.expected === undefined
            ? ""
            : String(client?.expected),
    currentCandidates:
        client?.currentCandidates === null || client?.currentCandidates === undefined
            ? ""
            : String(client?.currentCandidates),
    byPass: Boolean(client?.byPass),
});

const buildClientsKeyName = (clients = []) =>
    [...(Array.isArray(clients) ? clients : [])]
        .sort((left, right) =>
            normalizeClientId(left?.clientId).localeCompare(
                normalizeClientId(right?.clientId)
            )
        )
        .map((client) => `${client?.clientId}=${client?.isAssigned ? 1 : 0}:${client?.byPass ? 1 : 0}`)
        .join("|");

const buildSavePayload = (userId, clients = [], confirmed = false) => ({
    userId: String(userId ?? "").trim(),
    confirmed: Boolean(confirmed),
    clients: (Array.isArray(clients) ? clients : [])
        .filter((client) => Boolean(client?.isAssigned))
        .map((client) => ({
            clientId: normalizeClientId(client?.clientId),
            isAssigned: true,
            expected: String(client?.expected ?? ""),
            currentCandidates: String(client?.currentCandidates ?? ""),
            byPass: Boolean(client?.byPass),
        })),
});

const ClientAccessAddEditModal = ({
    clientAccessModalOpen,
    setClientAccessModalOpen,
    userRow = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userId, setUserId] = useState("");
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [showActivityFields, setShowActivityFields] = useState(false);
    const [showByPass, setShowByPass] = useState(false);
    const [clients, setClients] = useState([]);
    const [clientsKeyName, setClientsKeyName] = useState("");

    const resolvedUserId = String(
        userRow?.id ?? userRow?._id ?? userRow?.userId ?? userId ?? ""
    ).trim();

    const assignedClientsKeyName = useMemo(
        () => buildClientsKeyName(clients),
        [clients]
    );

    const clientListGridClass = useMemo(() => {
        if (showActivityFields && showByPass) return "client-access-grid--full";
        if (showActivityFields) return "client-access-grid--activity";
        if (showByPass) return "client-access-grid--bypass";
        return "client-access-grid--basic";
    }, [showActivityFields, showByPass]);

    useEffect(() => {
        if (!clientAccessModalOpen) return;

        const rowId = String(
            userRow?.id ?? userRow?._id ?? userRow?.userId ?? ""
        ).trim();
        if (!rowId) {
            toast.error("User id is missing");
            return;
        }

        let cancelled = false;

        const loadClientAccess = async () => {
            setLoading(true);
            try {
                const response = await fetchUserSystemClientAccess(rowId);
                if (cancelled) return;

                const data = response?.data ?? response ?? {};
                const clientRows = Array.isArray(data?.clients) ? data.clients : [];
                const normalizedClients = clientRows.map(normalizeClientRow);

                setUserId(String(data?.userId ?? rowId).trim());
                setUserName(
                    String(
                        data?.userName ??
                            userRow?.name ??
                            userRow?.userName ??
                            ""
                    ).trim()
                );
                setUserEmail(String(userRow?.email ?? "").trim());
                setShowActivityFields(Boolean(data?.showActivityFields));
                setShowByPass(Boolean(data?.showByPass));
                setClients(normalizedClients);
                setClientsKeyName(buildClientsKeyName(normalizedClients));
            } catch {
                if (!cancelled) {
                    setClients([]);
                    setClientsKeyName("");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadClientAccess();

        return () => {
            cancelled = true;
        };
    }, [clientAccessModalOpen, userRow]);

    const closeModal = () => {
        setClientAccessModalOpen(false);
    };

    const handleClientAssignedChange = (clientId, checked) => {
        setClients((prev) =>
            (prev || []).map((client) =>
                isSameClientId(client?.clientId, clientId)
                    ? { ...client, isAssigned: checked }
                    : client
            )
        );
    };

    const handleClientFieldChange = (clientId, field, value) => {
        setClients((prev) =>
            (prev || []).map((client) =>
                isSameClientId(client?.clientId, clientId)
                    ? { ...client, [field]: value }
                    : client
            )
        );
    };

    const isConfirmRequiredResponse = (response) => {
        if (!response || response?.success === true) return false;
        const code = String(response?.error?.code ?? "").toUpperCase();
        return code === "CONFIRM_REQUIRED";
    };

    const saveClientAccess = async (confirmed = false) => {
        const payload = buildSavePayload(resolvedUserId, clients, confirmed);
        return updateUserSystemClientAccess(resolvedUserId, payload);
    };

    const handleSave = async () => {
        if (!resolvedUserId) {
            toast.error("User id is missing");
            return;
        }

        setSaving(true);
        try {
            let response = await saveClientAccess(false);

            if (isConfirmRequiredResponse(response)) {
                const confirmMessage =
                    response?.error?.message ||
                    response?.message ||
                    "Please confirm to continue.";
                const swalResult = await Swal.fire({
                    title: "Confirm",
                    text: confirmMessage,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "OK",
                    cancelButtonText: "Cancel",
                    reverseButtons: true,
                    target: document.body,
                    allowOutsideClick: false,
                    didOpen: () => {
                        const el = document.querySelector(".swal2-container");
                        if (el) el.style.zIndex = 20000;
                    },
                });

                if (!swalResult?.isConfirmed) {
                    return;
                }

                response = await saveClientAccess(true);
            }

            if (response?.success) {
                toast.success(response?.message || "Client access updated successfully");
                closeModal();
                return;
            }

            toast.error(
                response?.error?.message ||
                    response?.message ||
                    "Unable to update client access"
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={clientAccessModalOpen}
            toggle={closeModal}
            backdrop="static"
            size="lg"
            centered
            zIndex={5000}
            className="modal-full-height client-access-add-edit-modal"
        >
            <ModalBody className="modal-height-fixed overflow-y-auto">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0 text-uppercase">Client Access</h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <div className="client-access-user-card mb-3">
                            <div className="client-access-user-field">
                                <div className="client-access-user-field__label">
                                    <i className="fa fa-envelope-o" aria-hidden="true" />
                                    Email
                                </div>
                                <div className="client-access-user-field__value">
                                    {userEmail || "—"}
                                </div>
                            </div>
                            <div className="client-access-user-field">
                                <div className="client-access-user-field__label">
                                    <i className="fa fa-user" aria-hidden="true" />
                                    Name
                                </div>
                                <div className="client-access-user-field__value">
                                    {userName || "—"}
                                </div>
                            </div>
                        </div>

                        <div className="client-access-list border rounded">
                            {(clients || []).length ? (
                                <>
                                    <div
                                        className={`client-access-list-header client-access-grid ${clientListGridClass} px-3 py-2`}
                                    >
                                        <span className="client-access-grid__checkbox" aria-hidden="true" />
                                        <span className="client-access-list-header__label">Client</span>
                                        {showActivityFields ? (
                                            <>
                                                <span className="client-access-list-header__label">
                                                    Expectation
                                                </span>
                                                <span className="client-access-list-header__label">
                                                    Cur. Pipeline
                                                </span>
                                            </>
                                        ) : null}
                                        {showByPass ? (
                                            <span className="client-access-list-header__label client-access-list-header__label--bypass">
                                                ByPass
                                            </span>
                                        ) : null}
                                    </div>
                                    {(clients || []).map((client, index) => {
                                    const rowKeyName = String(
                                        client?.keyName ?? client?.clientId ?? index
                                    ).trim();

                                    return (
                                        <div
                                            key={`client-access-row-${rowKeyName}`}
                                            className={`client-access-row client-access-grid ${clientListGridClass} align-items-center px-3 py-2 ${
                                                index % 2 === 0
                                                    ? "client-access-row-even"
                                                    : "client-access-row-odd"
                                            }`}
                                        >
                                            <div className="client-access-grid__checkbox">
                                                <Input
                                                    key={`client-access-assigned-${rowKeyName}-${assignedClientsKeyName}-${clientsKeyName}`}
                                                    type="checkbox"
                                                    className="form-check-input m-0"
                                                    defaultChecked={Boolean(client?.isAssigned)}
                                                    onChange={(event) =>
                                                        handleClientAssignedChange(
                                                            client?.clientId,
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            </div>
                                            <span
                                                className="client-access-row__name text-truncate"
                                                title={
                                                    client?.clientName ||
                                                    `Client ${client?.clientId}`
                                                }
                                            >
                                                {client?.clientName || `Client ${client?.clientId}`}
                                            </span>

                                            {showActivityFields ? (
                                                <>
                                                    <Input
                                                        type="text"
                                                        bsSize="sm"
                                                        className="client-access-field-input"
                                                        placeholder="Expectation"
                                                        value={client?.expected ?? ""}
                                                        onChange={(event) =>
                                                            handleClientFieldChange(
                                                                client?.clientId,
                                                                "expected",
                                                                event.target.value
                                                            )
                                                        }
                                                    />
                                                    <Input
                                                        type="text"
                                                        bsSize="sm"
                                                        className="client-access-field-input client-access-field-input--wide"
                                                        placeholder="Cur. Pipeline"
                                                        value={client?.currentCandidates ?? ""}
                                                        onChange={(event) =>
                                                            handleClientFieldChange(
                                                                client?.clientId,
                                                                "currentCandidates",
                                                                event.target.value
                                                            )
                                                        }
                                                    />
                                                </>
                                            ) : null}

                                            {showByPass ? (
                                                <div className="client-access-bypass d-flex justify-content-center">
                                                    <Input
                                                        key={`client-access-bypass-${rowKeyName}-${assignedClientsKeyName}-${clientsKeyName}`}
                                                        type="checkbox"
                                                        className="form-check-input m-0"
                                                        defaultChecked={Boolean(client?.byPass)}
                                                        onChange={(event) =>
                                                            handleClientFieldChange(
                                                                client?.clientId,
                                                                "byPass",
                                                                event.target.checked
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                                </>
                            ) : (
                                <div className="text-muted text-center py-4">
                                    No clients found
                                </div>
                            )}
                        </div>

                        <div className="text-end mt-4">
                            <Button color="primary" onClick={handleSave} disabled={saving}>
                                {saving ? <Spinner size="sm" className="me-1" /> : null}
                                Save
                            </Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

ClientAccessAddEditModal.propTypes = {
    clientAccessModalOpen: PropTypes.bool,
    setClientAccessModalOpen: PropTypes.func,
    userRow: PropTypes.object,
};

export default ClientAccessAddEditModal;
